<template>
  <!-- 模式一：经典老欢迎页，原封不动保留 -->
  <div v-if="$store.state.sysLayoutMode === 'classic'" class="welcome-bg">
    <div class="welcome-content">
      <div class="t t1">欢迎来到</div>
      <div class="t t2">{{ schoolName }}</div>
    </div>
  </div>

  <!-- 模式二：门户首页 — 青绿校园工作台（与翼智云通蓝白圆卡刻意区分） -->
  <div v-else class="portal-container">
    <div class="portal-bg-mesh" aria-hidden="true"></div>
    <div class="portal-bg-grid" aria-hidden="true"></div>

    <div class="portal-top-actions">
      <el-dropdown trigger="hover" placement="bottom-end">
        <button type="button" class="portal-action-btn portal-action-btn--ghost">
          <i class="el-icon-s-tools"></i>
          <span>布局</span>
        </button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item
            :class="{ 'is-portal-layout-active': $store.state.sysLayoutMode === 'classic' }"
            @click.native="switchLayout('classic')"
          >经典布局</el-dropdown-item>
          <el-dropdown-item
            :class="{ 'is-portal-layout-active': $store.state.sysLayoutMode === 'portal' }"
            @click.native="switchLayout('portal')"
          >门户布局</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
      <button type="button" class="portal-action-btn" @click="requestLogout">
        <i class="el-icon-switch-button"></i>
        <span>退出登录</span>
      </button>
    </div>

    <header class="portal-hero">
      <div class="portal-hero-kicker">智慧校园 · 应用中心</div>
      <h1 class="portal-hero-title">{{ schoolName }}</h1>
      <p class="portal-hero-subtitle">选择业务域进入，侧栏可在同域一级模块间切换；侧栏点星标可加入「我的常用」</p>
    </header>

    <!-- 我的常用：侧栏星标加减，此处直达（不用 section，避免全局 content-az section{height:100%} 撑高） -->
    <div class="portal-shortcuts">
      <div class="portal-shortcuts-head">
        <div class="portal-shortcuts-title">
          <i class="el-icon-star-on"></i>
          <span>我的常用</span>
          <em v-if="shortcutMenus.length">{{ shortcutMenus.length }}/8</em>
        </div>
        <span class="portal-shortcuts-hint">侧栏点星标添加或取消</span>
      </div>
      <div v-if="shortcutMenus.length" class="portal-shortcuts-row">
        <button
          v-for="item in shortcutMenus"
          :key="'sc-' + item.id"
          type="button"
          class="portal-shortcut-chip"
          :title="item.categoryName ? (item.categoryName + ' / ' + item.name) : item.name"
          @click="jumpTo(item.path)"
        >
          <span class="portal-shortcut-icon">
            <i :class="resolveMenuIconClass(item.iconCls)"></i>
          </span>
          <span class="portal-shortcut-name">{{ item.name }}</span>
        </button>
      </div>
      <div v-else class="portal-shortcuts-empty">
        暂无常用。进入业务域后，在左侧菜单点星标即可置顶。
      </div>
    </div>

    <div class="portal-section-label">业务域</div>

    <div class="portal-menu-grid">
      <button
        v-for="(card, idx) in portalMenuCards"
        :key="card.key"
        type="button"
        class="portal-menu-card"
        :class="'portal-accent-' + (idx % 5)"
        :style="{ '--reveal-i': idx }"
        @click="handleCardClick(card)"
      >
        <span class="menu-card-rail" aria-hidden="true"></span>
        <div class="menu-card-main">
          <div class="menu-card-head">
            <div class="menu-card-icon">
              <i :class="card.iconClass"></i>
            </div>
            <span class="menu-card-badge">{{ card.childCount }} 模块</span>
          </div>
          <div class="menu-card-title">{{ card.name }}</div>
          <div class="menu-card-foot">
            <span class="menu-card-desc">进入业务域</span>
            <i class="el-icon-right menu-card-arrow"></i>
          </div>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import { saveUserConfig, getUserConfig, getGlobalConfig } from '../api/modules/misc';
import { buildDefaultGroupsFromMenus } from '../constants/portalDeptDefaults';

// 系统默认预设的高频快捷功能 ID 列表 (现设为空，由用户自主收藏点亮)
const DEFAULT_SHORTCUTS = [];

export default {
  name: 'Welcome',
  data() {
    return {
      isEditingShortcuts: false, // 是否处于“自定义常用”编辑状态
      tempShortcutIds: [],       // 编辑状态下的临时常用快捷ID数组
      deptGroupsConfig: []       // 最终生效的处室分类与排序配置
    }
  },
  computed: {
    schoolName() {
      return this.$store.state.activeSchoolName || this.$store.state.DepartmentName || '管理平台'
    },
    // 当前登录账号的 ID
    currentUserUid() {
      const userStr = window.localStorage.getItem('user');
      return userStr ? (JSON.parse(userStr).uID || 0) : 0;
    },
    // 是否为系统超级管理员 (判定是否有配置全局大桌面处室板块排序及改名的权限)
    isSystemAdmin() {
      const userStr = window.localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      // 支持账号名 admin，或真实姓名/角色名为系统管理员的账户
      return user.uName === 'admin' || 
             user.uRealName === '系统管理员' || 
             (user.roleName && user.roleName.includes('系统管理员')) ||
             (user.role && user.role.includes('系统管理员'));
    },
    // 前端递归计算并提取全量一级功能卡片，兼容多级嵌套下的叶子页面跳转
    flatFunctionMenus() {
      const NavigationBar = JSON.parse(window.localStorage.getItem('router') || '[]');
      const list = [];
      
      // 递归寻找当前路由子树中第一个真正有具体页面组件的叶子路由路径
      const getFirstLeafPath = (node) => {
        // 过滤掉被隐藏的占位路由或者按钮级别的非页面路由项
        if (node.IsHide || node.IsButton || node.hidden === true || node.hidden === 'true') {
          return '';
        }
        
        // 检查子路由列表里是否包含“真实的下级子页面” (非按钮且未隐藏)
        const hasRealSubPage = node.children && node.children.some(child => {
          return !child.IsButton && !child.IsHide && child.hidden !== true && child.hidden !== 'true';
        });

        // 如果没有下级子页面，或者子页面全都是权限按钮，说明本节点就是可跳转的最终叶子页面
        if (!hasRealSubPage) {
          return (node.path && node.path !== '-' && node.path !== ' ') ? node.path : '';
        }
        
        // 否则深度递归查找可见 of 下级页面路径
        for (let i = 0; i < node.children.length; i++) {
          const path = getFirstLeafPath(node.children[i]);
          if (path) return path;
        }
        return '';
      };

      NavigationBar.forEach(item => {
        if (item.path === '/' || !item.name) return;
        if (item.name === '首页' || item.name === '大桌面版面配置') return;

        // 深度递归寻找该大类下第一个有效的叶子跳转路由
        const targetPath = getFirstLeafPath(item);
        // 可见子模块数量（排除按钮/隐藏），用于大卡片「N个子模块」
        const childCount = (item.children || []).filter(child => {
          return child && !child.IsButton && !child.IsHide && child.hidden !== true && child.hidden !== 'true';
        }).length;

        list.push({
          id: item.id,
          name: item.name,
          iconCls: item.iconCls || 'fa-folder-o',
          path: targetPath,
          childCount: childCount || 1
        });
      });
      return list;
    },
    // 递归提取整棵路由树里所有公开可见、可跳转的底层二级/三级真实叶子菜单项，用于匹配侧栏星标收藏
    flatLeafMenus() {
      const NavigationBar = JSON.parse(window.localStorage.getItem('router') || '[]');
      const list = [];
      
      const traverse = (node, topParentName = '') => {
        if (node.IsHide || node.IsButton || node.hidden === true || node.hidden === 'true') {
          return;
        }
        
        const hasRealSubPage = node.children && node.children.some(child => {
          return !child.IsButton && !child.IsHide && child.hidden !== true && child.hidden !== 'true';
        });
        
        if (!hasRealSubPage) {
          if (node.path && node.path !== '-' && node.path !== ' ') {
            list.push({
              id: node.id,
              name: node.name,
              iconCls: node.iconCls || 'fa-file-o',
              path: node.path,
              categoryName: topParentName // 传递一级父级大分类目录名称
            });
          }
        } else {
          node.children.forEach(child => traverse(child, topParentName || node.name));
        }
      };
      
      NavigationBar.forEach(item => traverse(item, item.name));
      return list;
    },
    // 渲染常用功能：合并一级与叶子菜单，按星标 ID 匹配（统一字符串防 long 精度问题）
    shortcutMenus() {
      const rawIds = this.$store.state.myShortcutIds || [];
      const ids = rawIds.map(id => String(id));
      if (!ids.length) return [];

      const list = [];
      const allPossibleMenus = [...this.flatFunctionMenus, ...this.flatLeafMenus];
      const menuMap = {};
      allPossibleMenus.forEach(menu => {
        if (!menu || menu.id == null) return;
        const sid = String(menu.id);
        if (!menuMap[sid]) menuMap[sid] = menu;
      });

      // 按收藏顺序展示，最多 8 个
      ids.slice(0, 8).forEach(sid => {
        const menu = menuMap[sid];
        if (menu && menu.path) list.push(menu);
      });
      return list;
    },
    // 基于后台及云端关联好的菜单 ID 数组过滤并平铺当前用户的权限功能
    departmentGroups() {
      // 核心修正：大桌面仅展示一级大类功能菜单卡片，避免因二级/三级碎叶子页面全部平铺导致界面过于混乱臃肿
      const allMenus = this.flatFunctionMenus;
      
      // 根据云端存取的处室分类、排序、显隐配置进行全局映射
      return this.deptGroupsConfig
        .filter(group => group.visible !== false)
        .map(groupConfig => {
          // 门户自定义序：严格按 menus 数组顺序组装（经典布局不走此处）
          const groupMenus = (groupConfig.menus || []).map(id => String(id));
          const menuMap = {};
          allMenus.forEach(m => {
            if (m && m.id != null) menuMap[String(m.id)] = m;
          });
          const matchingMenus = [];
          groupMenus.forEach(id => {
            if (menuMap[id]) matchingMenus.push(menuMap[id]);
          });
          
          return {
            key: groupConfig.key,
            name: groupConfig.name,  // 支持后台自定义改名
            icon: groupConfig.icon,
            orderSort: Number(groupConfig.orderSort || 0),
            list: matchingMenus
          };
        })
        .filter(g => g.list.length > 0)
        .sort((a, b) => a.orderSort - b.orderSort); // 只有包含当前登录账号可用页面权限的处室板块才渲染
    },
    /**
     * 首页大功能菜单：按版面分类汇总一级菜单
     * 一张卡 = 一个大功能；子模块数 = 该分类下挂接的一级菜单数
     */
    portalMenuCards() {
      return (this.departmentGroups || []).map(group => {
        const first = (group.list || []).find(m => m && m.path) || (group.list || [])[0] || {};
        const iconRaw = ((group.icon || '').toString().trim()) || first.iconCls || 'fa-folder-o';
        let iconClass = iconRaw;
        if (iconRaw.indexOf('el-icon-') === 0) {
          iconClass = iconRaw;
        } else if (iconRaw.indexOf('fa ') === 0) {
          iconClass = iconRaw;
        } else if (iconRaw.indexOf('fa-') === 0) {
          iconClass = ['fa', iconRaw];
        } else {
          iconClass = ['fa', iconRaw];
        }

        return {
          key: group.key,
          name: group.name,
          path: first.path || '',
          childCount: (group.list || []).length,
          iconClass,
          menuIds: (group.list || []).map(m => String(m.id))
        };
      }).filter(card => card.childCount > 0 && card.path);
    }
  },
  watch: {
    // 管理页保存后会写入 store，门户首页无需刷新即可生效
    '$store.state.portalDeptGroups': {
      deep: true,
      handler(val) {
        if (Array.isArray(val) && val.length > 0) {
          this.deptGroupsConfig = val;
        }
      }
    }
  },
  methods: {
    // 点击大功能菜单：进入该分类下首个一级模块；侧栏展示同分类全部一级菜单
    handleCardClick(card) {
      if (!card || !card.path) return;
      // 记录当前大功能，供侧栏按分类汇总一级菜单
      try {
        window.sessionStorage.setItem('portal_active_group', card.key || '');
      } catch (e) { /* ignore */ }
      this.jumpTo(card.path);
    },
    /** 兼容 fa / el-icon 图标类名 */
    resolveMenuIconClass(iconRaw) {
      const raw = ((iconRaw || '').toString().trim()) || 'fa-file-o';
      if (raw.indexOf('el-icon-') === 0) return raw;
      if (raw.indexOf('fa ') === 0) return raw;
      if (raw.indexOf('fa-') === 0) return ['fa', raw];
      return ['fa', raw];
    },
    /** 门户全屏首页：向上找到壳层 App 执行退出 */
    requestLogout() {
      let parent = this.$parent;
      while (parent && typeof parent.logout !== 'function') {
        parent = parent.$parent;
      }
      if (parent) parent.logout();
    },
    /** 门户全屏首页：切换经典/门户布局 */
    switchLayout(mode) {
      let parent = this.$parent;
      while (parent && typeof parent.changeLayout !== 'function') {
        parent = parent.$parent;
      }
      if (parent) parent.changeLayout(mode);
    },
    // 路由跳转
    jumpTo(path) {
      if (path) {
        this.$router.push(path);
      }
    },
    // 开启常用编辑模式
    enterEditMode() {
      this.tempShortcutIds = [...(this.$store.state.myShortcutIds || [])];
      this.isEditingShortcuts = true;
    },
    // 取消编辑模式
    cancelEditMode() {
      this.isEditingShortcuts = false;
      this.tempShortcutIds = [];
    },
    // 添加某个菜单到常用
    addShortcut(menuId) {
      if (this.tempShortcutIds.length >= 8) {
        this.$message.warning('置顶常用功能最多只能添加 8 个！');
        return;
      }
      if (!this.tempShortcutIds.includes(menuId)) {
        this.tempShortcutIds.push(menuId);
      }
    },
    // 从常用中移除某个菜单
    removeShortcut(menuId) {
      this.tempShortcutIds = this.tempShortcutIds.filter(id => id !== menuId);
    },
    // 保存并持久化同步常用菜单设置到云端数据库
    saveShortcuts() {
      const configValue = JSON.stringify(this.tempShortcutIds);
      
      // 同步保存到 Vuex 与本地 localStorage
      this.$store.commit('saveShortcutIds', this.tempShortcutIds);
      
      if (this.currentUserUid > 0) {
        saveUserConfig({
          userId: this.currentUserUid,
          configKey: 'shortcuts',
          configValue: configValue
        }).then(res => {
          if (res.data.success) {
            this.$message.success('您的个人常用功能快捷配置已永久保存至云端！');
          }
        }).catch(err => {
          console.error('保存常用设置至云端失败', err);
        });
      } else {
        this.$message.success('配置已保存至本地！');
      }
      
      this.isEditingShortcuts = false;
    },
    // 渐进式载入常用菜单与处室排版配置 (从云端优先拉取)
    initPortalConfigs() {
      if (this.$store.state.sysLayoutMode !== 'portal') return;

      // 出厂默认：仅对一级大类菜单做精确名称匹配（一菜单只归一处，与配置页一致）
      const defaultGroupsConfig = buildDefaultGroupsFromMenus(this.flatFunctionMenus);

      // 辅助清洗函数：物理剥离前缀残留的 emoji 图标，并统一 menus ID 为字符串
      const cleanGroupsConfig = (list) => {
        if (!Array.isArray(list)) return [];
        list.forEach(item => {
          if (item.name) {
            item.name = item.name.replace(/^[💻🏫👨‍🏫👮🏆🍕📊]\s*/g, '');
          }
          if (Array.isArray(item.menus)) {
            item.menus = item.menus.map(id => String(id));
          } else {
            item.menus = [];
          }
        });
        return list;
      };

      if (this.currentUserUid <= 0) {
        const localShortcuts = window.localStorage.getItem('my_shortcuts');
        this.$store.commit('saveShortcutIds', localShortcuts ? JSON.parse(localShortcuts) : [...DEFAULT_SHORTCUTS]);
        
        const localGroups = window.localStorage.getItem('global_dept_groups') || window.localStorage.getItem('my_dept_groups');
        this.deptGroupsConfig = cleanGroupsConfig(localGroups ? JSON.parse(localGroups) : defaultGroupsConfig);
        this.deptGroupsConfig.sort((a, b) => (a.orderSort || 0) - (b.orderSort || 0));
        // 同步至 store，供 App.vue 侧边栏权限过滤使用
        this.$store.commit('savePortalDeptGroups', this.deptGroupsConfig);
        return;
      }

      // 批量向后端通用配置表请求云端持久化数据
      // 获取快捷常用置顶设置 (每个人私有，userId 绑定为 currentUserUid)
      getUserConfig({
        key: 'shortcuts',
        userId: this.currentUserUid
      }).then(res => {
        if (res.data.success && res.data.response) {
          this.$store.commit('saveShortcutIds', JSON.parse(res.data.response));
        } else {
          const local = window.localStorage.getItem('my_shortcuts');
          this.$store.commit('saveShortcutIds', local ? JSON.parse(local) : [...DEFAULT_SHORTCUTS]);
        }
      }).catch(() => {
        const local = window.localStorage.getItem('my_shortcuts');
        this.$store.commit('saveShortcutIds', local ? JSON.parse(local) : [...DEFAULT_SHORTCUTS]);
      });

      // 获取大桌面分类及排序配置（全局共享，与 userId 无关）
      getGlobalConfig({
        key: 'dept_groups'
      }).then(res => {
        if (res.data.success && res.data.response) {
          const list = cleanGroupsConfig(JSON.parse(res.data.response));
          // 兼容历史老配置，补充 orderSort 字段
          list.forEach((item, idx) => {
            if (item.orderSort === undefined) {
              item.orderSort = idx + 1;
            }
          });
          this.deptGroupsConfig = list.sort((a, b) => (a.orderSort || 0) - (b.orderSort || 0));
        } else {
          const local = window.localStorage.getItem('global_dept_groups') || window.localStorage.getItem('my_dept_groups');
          this.deptGroupsConfig = cleanGroupsConfig(local ? JSON.parse(local) : defaultGroupsConfig);
          this.deptGroupsConfig.sort((a, b) => (a.orderSort || 0) - (b.orderSort || 0));
        }
        // 无论成功还是走兜底，均同步至 store，供 App.vue 侧边栏权限过滤使用
        this.$store.commit('savePortalDeptGroups', this.deptGroupsConfig);
      }).catch(() => {
        const local = window.localStorage.getItem('global_dept_groups') || window.localStorage.getItem('my_dept_groups');
        this.deptGroupsConfig = cleanGroupsConfig(local ? JSON.parse(local) : defaultGroupsConfig);
        this.deptGroupsConfig.sort((a, b) => (a.orderSort || 0) - (b.orderSort || 0));
        // 异常时也同步至 store
        this.$store.commit('savePortalDeptGroups', this.deptGroupsConfig);
      });
    }
  },
  created() {
    this.initPortalConfigs();
  }
}
</script>

<style scoped>
/* ================== 经典模式老页面样式 ================== */
.welcome-bg {
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-image: url('../assets/bg.jpg');
  background-size: cover;
  background-position: center;
}
.welcome-bg .t {
  font-size: 50px;
  font-weight: bold;
  text-align: center;
  color: #303133;
}
.welcome-bg .t2 {
  font-size: 70px;
  margin-top: 40px;
}

/* ================== 新版极简高级感 Portal 页面样式 ================== */
.portal-container {
  box-sizing: border-box;
  padding: 10px 16px 20px 16px;
  width: 100%;
  min-height: 100%;
  background-color: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  transition: background-color 0.3s ease;
  zoom: 1.1; /* 整体放大 10%，与浏览器 110% 缩放视觉等效 */
}

/* 紧凑型板块外框 */
.portal-section {
  background-color: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 12px 18px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.01);
  transition: all 0.3s ease;
}

/* 板块标题栏：紧凑左彩线排版 */
.section-title-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-left: 10px;
  border-left: 3px solid #3b82f6;
  height: 16px;
}

/* 常用快捷的垂直指示条使用琥珀金色 */
.shortcuts-section .section-title-bar {
  border-left-color: #f59e0b;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.section-title i {
  font-size: 14px;
}

.section-title .badge {
  background-color: rgba(59, 130, 246, 0.08);
  color: #3b82f6;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 700;
}

/* 常用快捷专属背景 */
.shortcuts-section {
  background-image: linear-gradient(180deg, rgba(245, 158, 11, 0.01) 0%, rgba(255, 255, 255, 0) 100%);
  border-color: rgba(245, 158, 11, 0.1);
}

/* 卡片通用紧凑网格排布 (增加一行能塞下的卡片数量) */
.functions-grid, .shortcuts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(115px, 1fr));
  gap: 12px;
}

.empty-placeholder {
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px 0;
  color: #94a3b8;
  font-size: 12px;
  border: 1px dashed #e2e8f0;
  border-radius: 6px;
}

/* 极致压缩后的扁平微悬浮白玉卡片 */
.function-card {
  position: relative;
  box-sizing: border-box;
  background-color: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.01);
  transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.function-card:hover {
  transform: translateY(-3px) scale(1.02);
  border-color: rgba(59, 130, 246, 0.35);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.06);
}

/* 常用置顶快捷卡片：赋予淡金渐变微光边框 */
.shortcut-card {
  border: 1px solid rgba(245, 158, 11, 0.12);
}

.shortcut-card:hover {
  border-color: rgba(245, 158, 11, 0.4);
  box-shadow: 0 6px 16px rgba(245, 158, 11, 0.07);
}

/* 编辑状态下卡片微震动与禁止跳转 */
.function-card.disabled {
  cursor: default;
  opacity: 0.65;
}
.function-card.disabled:hover {
  transform: none;
  box-shadow: none;
  border-color: #f1f5f9;
}
.function-card.already-shortcut {
  background-color: rgba(0, 0, 0, 0.01);
  border-style: dashed;
  opacity: 0.4;
}

/* 图标容器改小，采用 Flex 弹性盒，并引入各大部门专属配色 */
.card-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin-bottom: 6px;
  transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 强力重置 FontAwesome 图标本体在容器内的定位，彻底粉碎全局强加的 margin-right: 10px */
.card-icon-wrapper i {
  font-size: 13px;
  color: #64748b;
  margin-right: 0px !important;
  vertical-align: middle !important;
  line-height: 1 !important;
  display: inline-block !important;
  transition: color 0.22s ease;
}

.function-card:hover .card-icon-wrapper i {
  color: #ffffff !important;
}

/* ================== 🌈 8 大处室及快捷功能专属莫兰迪浅彩配色类库 ================== */
/* 1. 信息中心 */
.card-icon-wrapper.theme-info i { color: #3b82f6 !important; }
.card-icon-wrapper.theme-info { background-color: rgba(59, 130, 246, 0.07) !important; border-color: rgba(59, 130, 246, 0.12) !important; }

/* 0. 未分类 */
.card-icon-wrapper.theme-unclassified i { color: #f97316 !important; }
.card-icon-wrapper.theme-unclassified { background-color: rgba(249, 115, 22, 0.08) !important; border-color: rgba(249, 115, 22, 0.14) !important; }

/* 2. 装备处 */
.card-icon-wrapper.theme-equipment i { color: #8b5cf6 !important; }
.card-icon-wrapper.theme-equipment { background-color: rgba(139, 92, 246, 0.07) !important; border-color: rgba(139, 92, 246, 0.12) !important; }

/* 3. 教务处 */
.card-icon-wrapper.theme-teaching i { color: #6366f1 !important; }
.card-icon-wrapper.theme-teaching { background-color: rgba(99, 102, 241, 0.07) !important; border-color: rgba(99, 102, 241, 0.12) !important; }

/* 4. 安全处 */
.card-icon-wrapper.theme-security i { color: #06b6d4 !important; }
.card-icon-wrapper.theme-security { background-color: rgba(6, 182, 212, 0.07) !important; border-color: rgba(6, 182, 212, 0.12) !important; }

/* 5. 德育互动 */
.card-icon-wrapper.theme-moral i { color: #f59e0b !important; }
.card-icon-wrapper.theme-moral { background-color: rgba(245, 158, 11, 0.07) !important; border-color: rgba(245, 158, 11, 0.12) !important; }

/* 6. 后勤生活 */
.card-icon-wrapper.theme-general i { color: #10b981 !important; }
.card-icon-wrapper.theme-general { background-color: rgba(16, 185, 129, 0.07) !important; border-color: rgba(16, 185, 129, 0.12) !important; }

/* 7. 校长室 */
.card-icon-wrapper.theme-principal i { color: #ec4899 !important; }
.card-icon-wrapper.theme-principal { background-color: rgba(236, 72, 153, 0.07) !important; border-color: rgba(236, 72, 153, 0.12) !important; }

/* 8. 常用快捷置顶 */
.card-icon-wrapper.theme-shortcuts i { color: #d97706 !important; }
.card-icon-wrapper.theme-shortcuts { background-color: rgba(217, 119, 6, 0.07) !important; border-color: rgba(217, 119, 6, 0.12) !important; }

/* 鼠标悬停 (Hover) 状态下：图标颜色变白，背景过渡至饱和渐变，展现高级微发光 */
.function-card:hover .theme-info { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important; border-color: transparent !important; transform: scale(1.05); box-shadow: 0 3px 8px rgba(59, 130, 246, 0.18); }
.function-card:hover .theme-unclassified { background: linear-gradient(135deg, #f97316 0%, #ea580c 100%) !important; border-color: transparent !important; transform: scale(1.05); box-shadow: 0 3px 8px rgba(249, 115, 22, 0.2); }
.function-card:hover .theme-equipment { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important; border-color: transparent !important; transform: scale(1.05); box-shadow: 0 3px 8px rgba(139, 92, 246, 0.18); }
.function-card:hover .theme-teaching { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important; border-color: transparent !important; transform: scale(1.05); box-shadow: 0 3px 8px rgba(99, 102, 241, 0.18); }
.function-card:hover .theme-security { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%) !important; border-color: transparent !important; transform: scale(1.05); box-shadow: 0 3px 8px rgba(6, 182, 212, 0.18); }
.function-card:hover .theme-moral { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important; border-color: transparent !important; transform: scale(1.05); box-shadow: 0 3px 8px rgba(245, 158, 11, 0.22); }
.function-card:hover .theme-general { background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important; border-color: transparent !important; transform: scale(1.05); box-shadow: 0 3px 8px rgba(16, 185, 129, 0.18); }
.function-card:hover .theme-principal { background: linear-gradient(135deg, #ec4899 0%, #db2777 100%) !important; border-color: transparent !important; transform: scale(1.05); box-shadow: 0 3px 8px rgba(236, 72, 153, 0.18); }
.function-card:hover .theme-shortcuts { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important; border-color: transparent !important; transform: scale(1.05); box-shadow: 0 3px 8px rgba(245, 158, 11, 0.22); }

/* 紧凑型卡片字体，并在 Hover 时渐变深蓝 */
.card-name {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-align: center;
  word-break: break-all;
  transition: color 0.22s ease;
}

.function-card:hover .card-name {
  color: #1e3a8a;
}

/* 编辑状态下的角标 (添加/移除) */
.remove-badge, .add-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}
.remove-badge {
  background-color: #ef4444;
}
.add-badge {
  background-color: #10b981;
}
.remove-badge:hover, .add-badge:hover {
  transform: scale(1.15);
}

/* ================== ⚙️ 桌面分类设置弹窗专属样式 ================== */
.groups-config-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 500px;
  overflow-y: auto;
  padding: 6px;
}

.group-config-row {
  display: flex;
  align-items: center;
  gap: 14px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.25s ease;
}

.group-config-row:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.row-order-actions {
  display: flex;
  gap: 4px;
}

.row-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: rgba(59, 130, 246, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 15px;
}

.row-input {
  flex: 1;
}

.row-switch {
  display: flex;
  align-items: center;
}

@media screen and (max-width: 768px) {
  .portal-container {
    padding: 20px 15px;
  }
  .functions-grid, .shortcuts-grid {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  }
}

/* ================== 门户：青绿校园工作台（刻意区分翼智云通蓝白圆卡） ================== */
.portal-container {
  --portal-ink: #0b3d36;
  --portal-muted: #5f7a72;
  --portal-surface: #ffffff;
  --portal-line: #c9ddd6;
  --portal-bg: #e8f2ee;
  --portal-accent: #0f766e;
  position: relative;
  min-height: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 36px 40px 48px;
  background: var(--portal-bg);
  font-family: "Sora", "PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif;
  zoom: 1;
  overflow: auto;
  color: var(--portal-ink);
}

.portal-bg-mesh {
  pointer-events: none;
  position: absolute;
  inset: 0;
  background:
    radial-gradient(900px 420px at 8% -10%, rgba(15, 118, 110, 0.18), transparent 60%),
    radial-gradient(700px 380px at 92% 8%, rgba(217, 119, 6, 0.12), transparent 55%),
    radial-gradient(600px 500px at 70% 100%, rgba(13, 148, 136, 0.1), transparent 50%);
  z-index: 0;
}

.portal-bg-grid {
  pointer-events: none;
  position: absolute;
  inset: 0;
  opacity: 0.35;
  background-image:
    linear-gradient(rgba(11, 61, 54, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(11, 61, 54, 0.04) 1px, transparent 1px);
  background-size: 28px 28px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.55), transparent 88%);
  z-index: 0;
}

.portal-top-actions,
.portal-hero,
.portal-shortcuts,
.portal-section-label,
.portal-menu-grid {
  position: relative;
  z-index: 1;
}

.portal-top-actions {
  position: absolute;
  top: 20px;
  right: 28px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 10px;
}

.portal-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1.5px solid var(--portal-line);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: var(--portal-ink);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.portal-action-btn:hover {
  background: #ffffff;
  border-color: var(--portal-accent);
  color: var(--portal-accent);
}

.portal-action-btn--ghost {
  background: transparent;
}

.is-portal-layout-active {
  color: var(--portal-accent) !important;
  font-weight: 600;
}

.portal-hero {
  max-width: 920px;
  margin: 12px auto 32px 0;
  text-align: left;
  border-left: 4px solid var(--portal-accent);
  padding-left: 18px;
  color: var(--portal-ink);
}

.portal-hero-kicker {
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--portal-accent);
  margin-bottom: 8px;
}

.portal-hero-title {
  margin: 0;
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--portal-ink);
  text-shadow: none;
}

.portal-hero-subtitle {
  margin: 10px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--portal-muted);
  opacity: 1;
}

/* ---------- 我的常用 ---------- */
.portal-shortcuts {
  position: relative;
  z-index: 1;
  height: auto !important;
  min-height: 0 !important;
  overflow: visible !important;
  margin: 0 0 14px;
  padding: 8px 12px 10px;
  border: 1.5px solid #e0c48a;
  border-radius: 10px;
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 70%);
}

.portal-shortcuts-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.portal-shortcuts-title {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 700;
  color: #92400e;
}

.portal-shortcuts-title i {
  color: #d97706;
  font-size: 14px;
}

.portal-shortcuts-title em {
  font-style: normal;
  font-size: 11px;
  font-weight: 600;
  color: #b45309;
  background: #ffedd5;
  border: 1px solid #fdba74;
  border-radius: 999px;
  padding: 0 7px;
  line-height: 18px;
}

.portal-shortcuts-hint {
  font-size: 12px;
  color: #a16207;
  opacity: 0.85;
}

.portal-shortcuts-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.portal-shortcut-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 200px;
  height: 32px;
  padding: 0 10px 0 6px;
  border: 1.5px solid #f0d2ad;
  border-radius: 999px;
  background: #ffffff;
  color: #78350f;
  cursor: pointer;
  box-shadow: none;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.portal-shortcut-chip:hover,
.portal-shortcut-chip:focus {
  border-color: #d97706;
  background: #fff7ed;
  outline: none;
}

.portal-shortcut-chip:focus-visible {
  outline: 3px solid rgba(217, 119, 6, 0.35);
  outline-offset: 2px;
}

.portal-shortcut-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffedd5;
  color: #b45309;
  flex-shrink: 0;
}

.portal-shortcut-icon i {
  font-size: 11px;
  margin: 0 !important;
  line-height: 1 !important;
  color: inherit !important;
}

.portal-shortcut-name {
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.portal-shortcuts-empty {
  font-size: 12px;
  color: #a16207;
  line-height: 1.4;
  padding: 2px 0;
}

.portal-section-label {
  position: relative;
  z-index: 1;
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--portal-accent);
}

.portal-menu-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.portal-menu-card {
  --card-accent: #0f766e;
  position: relative;
  display: block;
  width: 100%;
  text-align: left;
  min-height: 148px;
  padding: 0;
  border: 1.5px solid var(--portal-line);
  border-radius: 12px;
  background: var(--portal-surface);
  box-shadow: none;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
  animation: portal-card-in 0.45s ease-out both;
  animation-delay: calc(var(--reveal-i, 0) * 45ms);
}

.portal-menu-card:hover,
.portal-menu-card:focus {
  border-color: var(--card-accent);
  background: #fbfffe;
  box-shadow: none;
  transform: translateY(-2px);
  outline: none;
}

.portal-menu-card:focus-visible {
  outline: 3px solid rgba(15, 118, 110, 0.35);
  outline-offset: 2px;
}

.portal-menu-card:active {
  transform: translateY(0);
}

.menu-card-rail {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: var(--card-accent);
}

.menu-card-main {
  padding: 18px 18px 16px 22px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.menu-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.menu-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e7f5f2;
  color: var(--card-accent);
  border: 1px solid #b7d8d2;
  box-shadow: none;
  flex-shrink: 0;
}

.menu-card-icon i {
  font-size: 20px;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  color: inherit !important;
}

.menu-card-badge {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: var(--card-accent);
  background: #eef8f5;
  border: 1px solid #c5ddd7;
  white-space: nowrap;
}

.menu-card-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--portal-ink);
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.menu-card-foot {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.menu-card-desc {
  margin: 0;
  font-size: 13px;
  color: var(--portal-muted);
}

.menu-card-arrow {
  font-size: 14px;
  color: var(--card-accent);
  opacity: 0.55;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.portal-menu-card:hover .menu-card-arrow {
  opacity: 1;
  transform: translateX(3px);
}

.portal-accent-0 { --card-accent: #0f766e; }
.portal-accent-0 .menu-card-icon { background: #e7f5f2; border-color: #b7d8d2; }
.portal-accent-0 .menu-card-badge { background: #eef8f5; border-color: #c5ddd7; }

.portal-accent-1 { --card-accent: #b45309; }
.portal-accent-1 .menu-card-icon { background: #fff7ed; border-color: #f0d2ad; }
.portal-accent-1 .menu-card-badge { background: #fffbeb; border-color: #f3e0bc; }

.portal-accent-2 { --card-accent: #0e7490; }
.portal-accent-2 .menu-card-icon { background: #ecfeff; border-color: #a8dce8; }
.portal-accent-2 .menu-card-badge { background: #f0fdfa; border-color: #b8e4df; }

.portal-accent-3 { --card-accent: #3f6212; }
.portal-accent-3 .menu-card-icon { background: #f7fee7; border-color: #d2e3a8; }
.portal-accent-3 .menu-card-badge { background: #f7fee7; border-color: #d9e7b4; }

.portal-accent-4 { --card-accent: #9a3412; }
.portal-accent-4 .menu-card-icon { background: #fff7ed; border-color: #ecc8b2; }
.portal-accent-4 .menu-card-badge { background: #fff1e8; border-color: #efd0bd; }

@keyframes portal-card-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media screen and (max-width: 1600px) {
  .portal-menu-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media screen and (max-width: 1200px) {
  .portal-menu-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .portal-hero-title {
    font-size: 32px;
  }
}

@media screen and (max-width: 768px) {
  .portal-container {
    padding: 56px 16px 24px;
  }
  .portal-hero {
    margin-bottom: 20px;
  }
  .portal-hero-title {
    font-size: 26px;
  }
  .portal-menu-grid {
    grid-template-columns: 1fr;
  }
  .portal-top-actions {
    top: 12px;
    right: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .portal-menu-card {
    animation: none;
    transition: none;
  }
  .portal-menu-card:hover,
  .portal-menu-card:focus {
    transform: none;
  }
  .portal-menu-card:hover .menu-card-arrow {
    transform: none;
  }
}
</style>
